import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Icon,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Paid as PaidIcon,
  MonetizationOn as MonetizationOnIcon,
  InfoOutlined as InfoOutlinedIcon,
  Savings as SavingsIcon,
} from "@mui/icons-material";

const VoucherInfoCard = ({
  voucherAmount,
  realAmountToApply,
  isEligible,
  voucherType = "Nearme",
  onUseVoucher,
  isApplied = false,
}) => {
  const theme = useTheme();

  const hasPartialApplication = isApplied && realAmountToApply < voucherAmount;
  const hasFullApplication = isApplied && realAmountToApply >= voucherAmount;

  const getBorderColor = () => {
    if (isApplied) {
      return hasFullApplication
        ? theme.palette.success.main
        : theme.palette.primary.main;
    }
    return isEligible ? theme.palette.warning.main : theme.palette.grey[300];
  };

  const getIcon = () => {
    if (isApplied) {
      return hasFullApplication ? CheckCircleIcon : PaidIcon;
    }
    return isEligible ? MonetizationOnIcon : InfoOutlinedIcon;
  };

  const getIconColor = () => {
    if (isApplied) {
      return hasFullApplication ? "success" : "primary";
    }
    return isEligible ? "warning" : "disabled";
  };

  const getTitleColor = () => {
    if (isApplied) {
      return hasFullApplication ? "success.main" : "primary.main";
    }
    return isEligible ? "warning.main" : "grey.700";
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: `1.5px solid ${getBorderColor()}`,
        borderRadius: 2,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        backgroundColor: "white",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Icon
            component={getIcon()}
            color={getIconColor()}
            sx={{ fontSize: 22, mr: 1 }}
          />
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: getTitleColor(),
            }}
          >
            {isApplied
              ? hasFullApplication
                ? "Coin Fully Applied"
                : "Coin Partially Applied"
              : "Available Coin"}
          </Typography>
        </Box>

        {/* Different messages based on application status */}
        {isApplied && hasFullApplication && (
          <Typography
            variant="body2"
            sx={{
              color: "success.dark",
              fontWeight: 600,
              mb: 1,
            }}
          >
            ₹{realAmountToApply.toFixed(2)} discount applied from your{" "}
            {voucherType} Coin!
          </Typography>
        )}

        {isApplied && hasPartialApplication && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "primary.dark",
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              ₹{realAmountToApply.toFixed(2)} discount applied (upto ₹
              {voucherAmount.toFixed(2)}) from your {voucherType} Coin!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                fontSize: "13px",
                mb: 1,
              }}
            >
              You could only use ₹{realAmountToApply.toFixed(2)} out of ₹
              {voucherAmount.toFixed(2)} available coins.
            </Typography>
          </>
        )}

        {!isApplied && isEligible && (
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 500,
              mb: 1,
            }}
          >
            Apply this Coin to save upto ₹{voucherAmount.toFixed(2)} on your
            order
          </Typography>
        )}

        {!isApplied && !isEligible && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "grey.600",
                mb: 0.5,
              }}
            >
              This {voucherType} Coin is not eligible for your current order.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "grey.500",
                fontSize: "13px",
                mb: 1,
              }}
            >
              Minimum order value or other criteria not met.
            </Typography>
          </>
        )}

        {/* Apply Button for eligible but not applied coins */}
        {isEligible && !isApplied && (
          <Button
            fullWidth
            variant="contained"
            onClick={onUseVoucher}
            sx={{
              mt: 1,
              mb: 1,
              py: 1.5,
              backgroundColor: "orange",
              color: "white",
              borderRadius: 1,
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "darkorange",
              },
            }}
          >
            Apply Coin
          </Button>
        )}

        {/* Success message for applied coins */}
        {isApplied && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: hasFullApplication ? "success.50" : "primary.50",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <SavingsIcon
              sx={{
                color: hasFullApplication ? "success.main" : "primary.main",
                fontSize: 18,
                mr: 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: hasFullApplication ? "success.dark" : "primary.dark",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {hasFullApplication
                ? `You saved ₹${realAmountToApply.toFixed(2)} with this Coin!`
                : `You saved ₹${realAmountToApply.toFixed(
                    2
                  )} (out of ₹${voucherAmount.toFixed(2)} available)`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VoucherInfoCard;
